import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ScatterChart,
  XAxis,
  YAxis,
  Scatter,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { Box, Text } from "@chakra-ui/react";
import YearSelection from "../YearSelection";
import { COLORS, TYPES_OF_METRICS } from "../../constants/metrics";
import Loader from "../Loader/Loader";

const CustomTooltip = ({ payload, label, active }) => {
  if (active && payload && payload.length) {
    const { xValue, yValue } = payload[0].payload;
    let label = "";
    if (xValue >= 0 && yValue >= 0) {
      label = "High Efficiency, High Market Presence";
    } else if (xValue >= 0 && yValue < 0) {
      label = "High Efficiency, Low Market Presence";
    } else if (xValue < 0 && yValue >= 0) {
      label = "Low Efficiency, High Market Presence";
    } else {
      label = "Low Efficiency, Low Market Presence";
    }
    return (
      <div
        className="custom-tooltip"
        style={{
          backgroundColor: "white",
          padding: "10px",
          border: "1px solid #ccc",
        }}
      >
        <p>{`${label}`}</p>
      </div>
    );
  }
  return null;
};

const PeerMatrix = ({
  currentType,
  currentMetric,
  patternData,
  setPatternValue,
  competitiveMenuLabel,
}) => {
  const dispatch = useDispatch();
  const { loading: clientLoading, data: clientData } = useSelector(
    (state) => state.clientReducer
  );
  const { loading: clientClusterWiseLoading, data: clientClusterWiseData } =
    useSelector((state) => state.clientClusterWiseReducer);
  const [selectedMetric, setSelectedMetric] = useState(currentMetric);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState();
  const [filteredData, setFilteredData] = useState([]);
  const [xLimit, setXLimit] = useState(["auto", "auto"]);
  const [yLimit, setYLimit] = useState(["auto", "auto"]);
  const [xRange, setXRange] = useState([]);
  const [yLabelLine, setYLabelLine] = useState(0);
  const [midX, setMidX] = useState(0);
  const [midY, setMidY] = useState(0);

  useEffect(() => {
    if (!clientLoading && clientData?._id) {
      setYears(clientData?.auditUploadYears);
      let year_size = clientData?.auditUploadYears.length;
      setSelectedYear(clientData?.auditUploadYears?.[year_size - 1]);
    }
  }, [dispatch, clientLoading]);

  useEffect(() => {
    setSelectedMetric(currentMetric);
  }, [currentMetric]);

  const handleYearChange = (value) => {
    setSelectedYear(value);
  };

  useEffect(() => {
    if (!clientClusterWiseData || !selectedYear) return;

    const selectedYearFilteredData =
      clientClusterWiseData?.clusterWiseData?.flatMap?.((cluster) => {
        return cluster.financialMetrics
          .filter((item) => item.year === parseInt(selectedYear))
          .map((item) => {
            const val = item[selectedMetric]
              ? item[selectedMetric].currNum / item[selectedMetric].currDenom
              : 0;

            const xVal =
              item.revenueFromOperations /
              clientClusterWiseData.totalRevenueFromOperationOfPeers[item.year];

            return {
              clientId: cluster.id,
              companyName: cluster.companyName,
              year: item.year,
              xValue: Number(Number(xVal * 100).toFixed(2)),
              yValue: TYPES_OF_METRICS?.marginAndCost?.value?.some?.(
                (m) => m?.label === selectedMetric
              )
                ? Number(Number(val * 100).toFixed(2))
                : Number(Number(val).toFixed(2)),
              yLabel: selectedMetric,
            };
          });
      });

    if (!selectedYearFilteredData || selectedYearFilteredData.length === 0) return;

   
    const xValues = selectedYearFilteredData.map((obj) => obj.xValue);
    const yValues = selectedYearFilteredData.map((obj) => obj.yValue);
    
    const xMin = Math.min(...xValues);
    const xMax = Math.max(...xValues);
    const yMin = Math.min(...yValues);
    const yMax = Math.max(...yValues);

    
    const xMiddle = (xMin + xMax) / 2;
    const yMiddle = (yMin + yMax) / 2;

   
    const xPadding = (xMax - xMin) * 0.1;
    const yPadding = (yMax - yMin) * 0.1;
    
    setXLimit([xMin - xPadding, xMax + xPadding]);
    setYLimit([yMin - yPadding, yMax + yPadding]);
    setMidX(xMiddle);
    setMidY(yMiddle);

    // Generate xRange for reference lines
    const xStep = (xMax - xMin) / 4;
    const xLimitRange = [];
    for (let i = xMin; i <= xMax; i += xStep) {
      xLimitRange.push(Number(i.toFixed(2)));
    }
    setXRange(xLimitRange);
    setYLabelLine((yMax - yMin) / 10);

    setFilteredData(selectedYearFilteredData);
    setPatternValue(
      selectedYearFilteredData
        .filter((sYFD) => sYFD.clientId === clientData._id)
        .map((sYFD) => ({
          clientId: sYFD.clientId,
          metricValue: sYFD[selectedMetric],
        }))?.[0]
    );
  }, [selectedMetric, selectedYear, clientClusterWiseData]);
 

  return (
    <div style={{ width: "100%" }}>
      <Box
        py={5}
        px={3}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Text textTransform="uppercase" fontWeight={600} fontSize={18}>
          Peer Metrics
        </Text>
        <YearSelection
          years={years}
          selectedYear={selectedYear}
          onSelectYear={handleYearChange}
        />
      </Box>
      {clientClusterWiseLoading ? (
        <Box h={"200px"}>
          <Loader />
        </Box>
      ) : (
        <Box padding={"15px"}>
    <ResponsiveContainer width="100%" height={500}>
  <ScatterChart
    margin={{ top: 20, right: 30, bottom: 40, left: 60 }}
  >
   
    <ReferenceLine y={midY} stroke="#000000" strokeWidth={1.5} />
    <ReferenceLine x={midX} stroke="#000000" strokeWidth={1.5} />

   
    <XAxis
  dataKey="xValue"
  type="number"
  domain={xLimit}
  unit={
    TYPES_OF_METRICS?.marginAndCost?.value?.some?.(
      (m) => m?.label === selectedMetric
    )
      ? "%"
      : ""
  }
  axisLine={false}
  tickLine={false}
  label={null} 
  tick={({ x, payload }) => (
    <g transform={`translate(${x},${midY + 230})`}> 
      <text
        x={6}
        y={0}
        dy={9}
        textAnchor="middle"
        fill="#333" 
        fontSize={12}
      >
        {payload?.value}
      </text>
    </g>
  )}
/>


    <YAxis
      dataKey="yValue"
      type="number"
      domain={yLimit}
      axisLine={false}
      tick={false} 
      label={null} 
    />

    <Tooltip
      cursor={{ strokeDasharray: "3 3" }}
      content={<CustomTooltip />}
    />

    {Object.keys(competitiveMenuLabel).map((metric) => (
      <Scatter
        key={metric}
        name={competitiveMenuLabel[metric]}
        data={filteredData?.map?.((data) => ({
          ...data,
          fill:
            data?.companyName === clientData?.companyName
              ? COLORS?.[patternData?.interpretation?.color]?.textColor ||
                "#6E68DE"
              : "#6E68DE",
        }))}
        dataKey={selectedMetric}
      />
    ))}
  </ScatterChart>
</ResponsiveContainer>


        </Box>
      )}
    </div>
  );
};

export default PeerMatrix;